import { reactive, readonly } from 'vue';

const state = reactive({
  activeRequests: 0,
  blockingRequests: 0,
  importActive: false,
  message: '',
  detail: '',
  progress: null,
  startedAt: null,
});

const activeRequestIds = new Set();
const blockingRequestIds = new Set();

function isImportRequest(config = {}) {
  const method = String(config.method || '').toLowerCase();
  const url = String(config.url || '').toLowerCase();
  const hasFilePayload = typeof FormData !== 'undefined' && config.data instanceof FormData;

  return method === 'post'
    && hasFilePayload
    && (url.includes('/import') || url.includes('/bulk-import'));
}

// Table-driven import-overlay labels. Order matters: longer / more-specific
// substrings must appear before shorter prefixes so e.g. `animal-health/records/import`
// wins over a hypothetical generic `health/import`, and `species-checklist/import`
// wins over `species/import`.
//
// To add a label for a new import URL, just append one row:
//   { match: 'foo/import', title: 'Importing foos', detail: '...' }
const IMPORT_LABELS = [
  // --- Cat B / specially-labelled flows (mortality, species-checklist, etc.) ---
  { match: 'wildlife-mortality/import',           title: 'Importing mortality data',
    detail: 'Uploading and processing the KWS mortality CSV. Keep this page open until the import completes.' },
  { match: 'species-checklist/import',            title: 'Importing species checklist',
    detail: 'Uploading and upserting the species-checklist CSV. Keep this page open until the import completes.' },
  { match: 'conservation-areas/import',           title: 'Importing conservation areas',
    detail: 'Uploading and upserting the conservation-areas CSV. Keep this page open until the import completes.' },
  { match: 'animal-health/records/import',        title: 'Importing animal health records',
    detail: 'Uploading and upserting the animal-health-records CSV. Keep this page open until the import completes.' },
  { match: 'wildlife-translocations/import',      title: 'Importing wildlife translocations',
    detail: 'Uploading and upserting the translocations CSV. Keep this page open until the import completes.' },
  { match: 'wildlife-corridors/import',           title: 'Importing wildlife corridors',
    detail: 'Uploading and upserting the wildlife-corridors CSV. Keep this page open until the import completes.' },
  { match: 'publications/import',                 title: 'Importing research publications',
    detail: 'Uploading and upserting the publications CSV. Keep this page open until the import completes.' },
  { match: 'virtual-museums/import',              title: 'Importing virtual-museum entries',
    detail: 'Uploading and upserting the virtual-museums CSV. Keep this page open until the import completes.' },
  { match: 'endangered-species/import',           title: 'Importing endangered species individuals',
    detail: 'Uploading and upserting the endangered-species CSV. Keep this page open until the import completes.' },
  { match: 'ongoing-research/import',             title: 'Importing ongoing-research projects',
    detail: 'Uploading and upserting the ongoing-research CSV. Keep this page open until the import completes.' },

  // --- HWC family (bulk-import on lookup tables + conflict records) ---
  { match: 'hwc/counties/bulk-import',            title: 'Importing HWC counties',
    detail: 'Uploading the counties CSV. Per-row savepoints isolate any failures so the batch survives.' },
  { match: 'hwc/sub-counties/bulk-import',        title: 'Importing HWC sub-counties',
    detail: 'Uploading the sub-counties CSV (county_name parent lookup happens server-side).' },
  { match: 'hwc/constituencies/bulk-import',      title: 'Importing HWC constituencies',
    detail: 'Uploading the constituencies CSV (county + sub-county hierarchy validated server-side).' },
  { match: 'hwc/wards/bulk-import',               title: 'Importing HWC wards',
    detail: 'Uploading the wards CSV (county + constituency + sub-county hierarchy validated server-side).' },
  { match: 'hwc/conflicts/import',                title: 'Importing HWC conflict records',
    detail: 'Uploading the HWC conflicts CSV. Keep this page open until the import completes.' },

  // --- Animal census + animal health ---
  { match: 'animal-census-categories/import',     title: 'Importing animal-census categories',
    detail: 'Uploading the census categories CSV.' },
  { match: 'animal-census-count-areas/import',    title: 'Importing animal-census count areas',
    detail: 'Uploading the count-areas CSV (GPS coordinates optional).' },
  { match: 'animal-census/import',                title: 'Importing census data',
    detail: 'Uploading and processing the Excel file. Keep this page open until the import completes.' },

  // --- Environment / data tables ---
  { match: 'climate-data/import',                 title: 'Importing climate data',
    detail: 'Uploading the climate-data CSV. Upsert keyed on (ecosystem_id, measurement_time).' },
  { match: 'threat-analysis/import',              title: 'Importing threat-analysis records',
    detail: 'Uploading the threat-analysis CSV. Upsert keyed on (threat_type, date_reported).' },
  { match: 'conservation-projects/import',        title: 'Importing conservation projects',
    detail: 'Uploading the conservation-projects CSV. Upsert keyed on project_name.' },
  { match: 'marine-ecosystems/import',            title: 'Importing marine ecosystems',
    detail: 'Uploading the marine-ecosystems CSV. Upsert keyed on reef_name.' },
  { match: 'marine-species/import',               title: 'Importing marine species data',
    detail: 'Uploading the marine-species CSV (append-only).' },
  { match: 'water-quality/import',                title: 'Importing water-quality data',
    detail: 'Uploading the water-quality time-series CSV.' },
  { match: 'parks/import',                        title: 'Importing parks',
    detail: 'Uploading the parks CSV. Upsert keyed on name.' },
  { match: 'educational-materials/import',        title: 'Importing educational materials',
    detail: 'Uploading the educational-materials CSV. Upsert keyed on title.' },
  { match: 'injury-types/import',                 title: 'Importing injury types',
    detail: 'Uploading the injury-types CSV. Upsert keyed on name.' },
  { match: 'habitat-monitoring/import',           title: 'Importing habitat assessments',
    detail: 'Uploading the habitat-monitoring CSV. Upsert keyed on (site_name, assessment_date).' },
  { match: 'invasive-plants/bulk-import',         title: 'Importing invasive-plant records',
    detail: 'Uploading the invasive-plant records JSON payload (append-only).' },

  // --- Settings / admin ---
  { match: 'roles/import',                        title: 'Importing roles',
    detail: 'Uploading the roles CSV. Upsert keyed on name.' },
  { match: 'users/import',                        title: 'Importing users',
    detail: 'Uploading the users CSV. Upsert keyed on email — passwords are NEVER overwritten on update.' },
  { match: 'cms/settings/import',                 title: 'Importing CMS settings',
    detail: 'Uploading the CMS settings CSV. Upsert keyed on key.' },
  { match: 'cms/tags/import',                     title: 'Importing CMS tags',
    detail: 'Uploading the CMS tags CSV. Upsert keyed on name.' },
  { match: 'ecosystems/import',                   title: 'Importing ecosystems',
    detail: 'Uploading the ecosystems CSV via the shared bulk_operations helper.' },
  { match: 'species/import',                      title: 'Importing species records',
    detail: 'Uploading the species CSV via the shared bulk_operations helper.' },
  { match: 'stakeholders/import',                 title: 'Importing stakeholders',
    detail: 'Uploading the stakeholders CSV via the shared bulk_operations helper.' },
];


function describeRequest(config = {}) {
  const url = String(config.url || '');
  for (const { match, title, detail } of IMPORT_LABELS) {
    if (url.includes(match)) {
      return { message: title, detail };
    }
  }
  return {
    message: 'Importing data',
    detail: 'Uploading and processing the file. Keep this page open until the import completes.',
  };
}

function begin(config = {}) {
  const id = Symbol('request');
  config.__requestActivityId = id;
  activeRequestIds.add(id);
  state.activeRequests = activeRequestIds.size;

  if (config.showBlockingLoader || isImportRequest(config)) {
    const labels = describeRequest(config);
    blockingRequestIds.add(id);
    state.blockingRequests = blockingRequestIds.size;
    state.importActive = true;
    state.message = config.loadingMessage || labels.message;
    state.detail = config.loadingDetail || labels.detail;
    state.progress = null;
    state.startedAt = state.startedAt || Date.now();
  }

  return config;
}

function finish(config = {}) {
  const id = config.__requestActivityId;
  if (!id) return;

  activeRequestIds.delete(id);
  blockingRequestIds.delete(id);

  state.activeRequests = activeRequestIds.size;
  state.blockingRequests = blockingRequestIds.size;

  if (blockingRequestIds.size === 0) {
    state.importActive = false;
    state.message = '';
    state.detail = '';
    state.progress = null;
    state.startedAt = null;
  }
}

function setProgress(progressEvent) {
  if (!progressEvent || !progressEvent.total) {
    state.progress = null;
    return;
  }
  state.progress = Math.min(
    100,
    Math.round((progressEvent.loaded * 100) / progressEvent.total)
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (!state.importActive) return;
    event.preventDefault();
    event.returnValue = '';
  });
}

export const requestActivity = readonly(state);
export const requestActivityController = {
  begin,
  finish,
  setProgress,
  isImportRequest,
};
